import { useEffect, useRef, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X, Languages } from "lucide-react";
import { useLang } from "@/i18n";

export default function Navbar() {
  const { t, lang, setLang } = useLang();
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const [active, setActive] = useState("");
  const headerRef = useRef(null);

  useEffect(() => {
    const ids = ["services", "investors", "packages", "faq", "contact"];
    const onScroll = () => {
      setScrolled(window.scrollY > 40);
      let current = "";
      for (const id of ids) {
        const el = document.getElementById(id);
        if (el && el.getBoundingClientRect().top <= 140) current = id;
      }
      setActive(current);
    };
    window.addEventListener("scroll", onScroll, { passive: true });
    onScroll();
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    if (!open) return;
    const onDown = (e) => {
      if (headerRef.current && !headerRef.current.contains(e.target)) setOpen(false);
    };
    const onPop = () => setOpen(false);
    history.pushState({ bsMenu: true }, "");
    document.addEventListener("pointerdown", onDown);
    window.addEventListener("popstate", onPop);
    return () => {
      document.removeEventListener("pointerdown", onDown);
      window.removeEventListener("popstate", onPop);
    };
  }, [open]);

  const links = [
    { href: "#services", label: t("nav_services") },
    { href: "#investors", label: t("nav_investors") },
    { href: "#packages", label: t("nav_packages") },
    { href: "#faq", label: t("nav_faq") },
    { href: "#contact", label: t("nav_contact") },
  ];

  return (
    <header
      ref={headerRef}
      data-testid="main-navbar"
      className={`fixed top-0 inset-x-0 z-50 transition-[background-color,box-shadow,border-color] duration-500 ${
        scrolled ? "glass shadow-2xl shadow-navy-950/50" : "bg-transparent border-transparent"
      }`}
    >
      <div className="max-w-7xl mx-auto px-6 lg:px-10 flex items-center justify-between h-20">
        <a href="#top" data-testid="navbar-logo" className="flex items-center gap-3 group">
          <img src="/logo.png" alt="Barq Safe" className="h-12 w-12 object-contain rounded-xl bg-white/95 p-1 transition-transform duration-300 group-hover:scale-105" />
          <div className="leading-tight">
            <span className="block font-display font-extrabold text-lg text-white">{t("brand")}</span>
            <span className="block text-[11px] tracking-widest text-gold">{t("tagline")}</span>
          </div>
        </a>

        <nav className="hidden lg:flex items-center gap-8" data-testid="navbar-links">
          {links.map((l) => (
            <a key={l.href} href={l.href} data-testid={`nav-link-${l.href.slice(1)}`}
              className={`relative text-sm transition-colors duration-300 hover:text-gold ${active === l.href.slice(1) ? "text-gold" : "text-white/70"}`}>
              {l.label}
              {active === l.href.slice(1) && (
                <span className="absolute -bottom-2 left-1/2 -translate-x-1/2 w-1.5 h-1.5 rotate-45 bg-gold" data-testid={`nav-active-${l.href.slice(1)}`} />
              )}
            </a>
          ))}
        </nav>

        <div className="flex items-center gap-3">
          <button
            data-testid="lang-toggle-button"
            onClick={() => setLang(lang === "ar" ? "en" : "ar")}
            className="flex items-center gap-2 px-3 py-2 rounded-full border border-gold/30 text-gold text-xs font-semibold hover:bg-gold/10 transition-colors duration-300"
          >
            <Languages size={14} />
            {lang === "ar" ? "EN" : "عربي"}
          </button>
          <a href="#contact" data-testid="navbar-cta-button"
            className="hidden md:inline-flex px-5 py-2.5 rounded-full bg-gold text-navy-900 text-sm font-bold hover:bg-gold-light transition-colors duration-300">
            {t("cta_consult")}
          </a>
          <button data-testid="mobile-menu-button" onClick={() => setOpen(!open)} className="lg:hidden text-white p-2" aria-label="menu">
            {open ? <X size={22} /> : <Menu size={22} />}
          </button>
        </div>
      </div>

      <AnimatePresence>
        {open && (
          <motion.nav
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            exit={{ opacity: 0, height: 0 }}
            className="lg:hidden glass border-t border-white/10 overflow-hidden"
            data-testid="mobile-menu"
          >
            <div className="px-6 py-4 flex flex-col gap-4">
              {links.map((l) => (
                <a key={l.href} href={l.href} onClick={() => setOpen(false)} data-testid={`mobile-nav-${l.href.slice(1)}`}
                  className={`transition-colors py-1 hover:text-gold ${active === l.href.slice(1) ? "text-gold font-semibold" : "text-white/80"}`}>
                  {l.label}
                </a>
              ))}
              <a href="#contact" onClick={() => setOpen(false)} data-testid="mobile-navbar-cta"
                className="mt-2 inline-flex justify-center px-5 py-3 rounded-full bg-gold text-navy-900 text-sm font-bold">
                {t("cta_consult")}
              </a>
            </div>
          </motion.nav>
        )}
      </AnimatePresence>
    </header>
  );
}
